import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import Button from '../general/Button';
import SupportMessageBody from './SupportMessageBody';
import SupportSources from './SupportSources';
import { SupportMessage } from '../../api/support/types';

interface SupportMessageItemProps {
    message: SupportMessage;
    onRate: (messageId: number, rating: 'up' | 'down') => void;
    onEscalate: () => void;
    canEscalate: boolean;
    /** L'objet n'est montré que sur le premier message du fil. */
    showSubject?: boolean;
}

export default function SupportMessageItem({ message, onRate, onEscalate, canEscalate, showSubject = true }: SupportMessageItemProps) {
    const { t } = useTranslation('support');
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const showEscalate = isAssistant && canEscalate && (message.answered === false || message.is_error);

    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${isUser ? 'bg-primary-color text-white' : 'bg-main-color-light text-main-color-dark'}`}>
                {message.role === 'agent' && (
                    <span className="mb-1 inline-block rounded-full bg-primary-color px-2 py-0.5 text-xs text-white">
                        {t('team_badge', { name: message.author_name ?? '' })}
                    </span>
                )}
                {showSubject && message.subject && <p className="mb-1 text-xs text-text-color-light">{message.subject}</p>}
                {/* Mise en forme en éléments React : le corps vient d'un LLM, jamais de HTML injecté. */}
                <SupportMessageBody body={message.body} />

                <SupportSources sources={message.sources} className="mt-2 space-y-1 border-t border-main-color/20 pt-2 text-xs" />

                {/* Boutons icône à bascule (aria-pressed) : Button ne gère pas ce cas. */}
                {isAssistant && !message.is_error && !message.is_ceiling && (
                    <div className="mt-2 flex gap-2">
                        <button type="button" aria-label={t('rate_up')} aria-pressed={message.rating === 1}
                            onClick={() => onRate(message.id, 'up')}
                            className={`rounded p-1 hover:bg-main-bg-color ${message.rating === 1 ? 'bg-white text-primary-color' : ''}`}>
                            <HandThumbUpIcon className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label={t('rate_down')} aria-pressed={message.rating === -1}
                            onClick={() => onRate(message.id, 'down')}
                            className={`rounded p-1 hover:bg-main-bg-color ${message.rating === -1 ? 'bg-white text-primary-color' : ''}`}>
                            <HandThumbDownIcon className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {showEscalate && (
                    <div className="mt-2">
                        <Button type="primary-outline" size="sm" elementType="button"
                            attributes={{ onClick: onEscalate }} text={t('talk_to_team')} />
                    </div>
                )}
            </div>
        </div>
    );
}
